import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Banner, bannerService } from '../../services/banner.service';

export interface HeroCarouselProps {
  items?: CarouselItem[];
}

interface CarouselItem {
  id: string;
  title: string;
  description: string;
  image: string;
  buttonText: string;
  buttonLink: string;
}

// Default carousel items if none are provided
const defaultItems: CarouselItem[] = [
  {
    id: '1',
    title: 'Trái cây tươi ngon',
    description: 'Lựa chọn tốt nhất cho sức khỏe gia đình bạn',
    image:
      'https://images.unsplash.com/photo-1490474418585-ba9bad8fd0ea?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80',
    buttonText: 'Xem sản phẩm',
    buttonLink: '/categories/trai-cay-viet-nam',
  },
  {
    id: '2',
    title: 'Trái cây nhập khẩu',
    description: 'Trải nghiệm hương vị đặc trưng từ khắp nơi trên thế giới',
    image:
      'https://images.unsplash.com/photo-1610832958506-aa56368176cf?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80',
    buttonText: 'Khám phá ngay',
    buttonLink: '/categories/trai-cay-nhap-khau',
  },
  {
    id: '3',
    title: 'Combo trái cây tiết kiệm',
    description: 'Tiết kiệm đến 30% với các gói combo hấp dẫn',
    image:
      'https://images.unsplash.com/photo-1519996529931-28324d5a630e?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80',
    buttonText: 'Mua ngay',
    buttonLink: '/categories/combo-trai-cay',
  },
];

const HeroCarousel: React.FC<HeroCarouselProps> = ({ items: propItems }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [items, setItems] = useState<CarouselItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch banners from API
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        setLoading(true);
        const banners = await bannerService.getActiveBanners();

        if (banners && banners.length > 0) {
          // Map Banner[] to CarouselItem[]
          const carouselItems = banners
            .sort((a, b) => a.index - b.index) // Sort by index
            .map((banner) => ({
              id: banner.id,
              title: banner.name,
              description: banner.description || '',
              image: banner.image,
              buttonText: '',
              buttonLink: banner.link || '/',
            }));

          setItems(carouselItems);
        } else {
          // Fallback to default items if no banners are returned
          setItems(propItems || defaultItems);
        }
      } catch (error) {
        console.error('Error fetching banners:', error);
        // Fallback to default items on error
        setItems(propItems || defaultItems);
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
  }, [propItems]);

  const goToSlide = (index: number) => {
    if (isAnimating) return;

    setIsAnimating(true);
    setActiveIndex(index);

    // Reset animation flag after transition completes
    setTimeout(() => {
      setIsAnimating(false);
    }, 500); // Should match the CSS transition duration
  };

  const goToPrev = () => {
    const newIndex = activeIndex === 0 ? items.length - 1 : activeIndex - 1;
    goToSlide(newIndex);
  };

  const goToNext = () => {
    const newIndex = activeIndex === items.length - 1 ? 0 : activeIndex + 1;
    goToSlide(newIndex);
  };

  // Auto-advance slides
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isAnimating && items.length > 1) {
        goToNext();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [activeIndex, isAnimating, items.length]);

  // If loading, show a placeholder
  if (loading) {
    return (
      <section className='relative h-[400px] md:h-[500px] overflow-hidden bg-gray-200 animate-pulse'>
        <div className='container mx-auto px-4 h-full flex flex-col justify-center items-center'>
          <div className='w-48 h-8 bg-gray-300 rounded-md mb-4'></div>
          <div className='w-64 h-4 bg-gray-300 rounded-md'></div>
        </div>
      </section>
    );
  }

  // If no items, don't render the carousel
  if (items.length === 0) {
    return null;
  }

  return (
    <section className='relative h-[400px] md:h-[500px] overflow-hidden'>
      {/* Slides */}
      <div className='h-full'>
        {items.map((item, index) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-500 ${
              index === activeIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            <Link
              to={item.buttonLink}
              className='block w-full h-full'
              aria-label={`Đi đến ${item.title}`}
            >
              <div
                className='w-full h-full'
                style={{
                  backgroundImage: `url(${item.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                <div className='container mx-auto px-4 h-full flex flex-col justify-center items-start'>
                  <div className='max-w-lg'>
                    {/* <h2 className='text-white text-3xl md:text-4xl font-bold mb-4'>
                      {item.title}
                    </h2> */}
                    {item.description && (
                      <p className='text-white text-lg mb-6'>
                        {item.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          </div>
        ))}
      </div>

      {/* Only show navigation buttons if there's more than one slide */}
      {items.length > 1 && (
        <>
          <button
            onClick={goToPrev}
            className='absolute left-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/20 hover:bg-white/40 text-white rounded-full p-2 backdrop-blur-sm transition duration-300'
            aria-label='Previous slide'
          >
            <ChevronLeftIcon className='w-6 h-6' />
          </button>
          <button
            onClick={goToNext}
            className='absolute right-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/20 hover:bg-white/40 text-white rounded-full p-2 backdrop-blur-sm transition duration-300'
            aria-label='Next slide'
          >
            <ChevronRightIcon className='w-6 h-6' />
          </button>

          {/* Indicators */}
          <div className='absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20 flex space-x-2'>
            {items.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === activeIndex
                    ? 'w-8 bg-primary'
                    : 'bg-white/60 hover:bg-white'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
};

export default HeroCarousel;
